export interface AppCopy {
  voice: {
    principles: string[];
    avoid: string[];
  };
  home: {
    badge: string;
    utilityBadge: string;
    heroEyebrow: string;
    heroTitle: string;
    heroDescription: string;
    heroChips: string[];
    footerEmpty: string;
    footerSaved: (count: number) => string;
    dashboardButton: string;
    coursesButton: string;
    adminButton: string;
    signOutButton: string;
    coursesTeaserEyebrow: string;
    coursesTeaserTitle: string;
    coursesTeaserDescription: string;
    coursesTeaserCta: string;
    coursesTeaserMeta: string;
  };
  topicInput: {
    eyebrow: string;
    title: string;
    placeholder: string;
    submitIdle: string;
    submitLoading: string;
    surprise: string;
    popularLabel: string;
  };
  loading: {
    defaultMessage: string;
    customMessage: (topic: string) => string;
    eyebrow: string;
    title: string;
    helper: string;
  };
  card: {
    topicEyebrow: string;
    loadingBadge: string;
    styleHint: string;
    styleHintDesktop: string;
    previousHint: string;
    nextHint: string;
    helper: string;
    saveLabel: string;
    unsaveLabel: string;
    cardLabel: (index: number, total: number) => string;
    controlsTitle: string;
    controlsDescription: string;
    controlsCloseHint: string;
    controls: {
      backdropTitle: string;
      typeTitle: string;
      contrastTitle: string;
      themes: Record<"midnight" | "aurora" | "ember" | "petal" | "sage", string>;
      fonts: Record<"sans" | "display" | "mono", string>;
      textTones: Record<"default" | "soft" | "high-contrast", string>;
    };
  };
  options: {
    eyebrow: string;
    title: (topic: string) => string;
    description: string;
    followUpsCount: (count: number) => string;
    noFollowUps: string;
    mobileHint: string;
    selectCta: string;
    regenerate: string;
    explore: string;
    empty: string;
  };
  auth: {
    trigger: string;
    title: string;
    description: string;
    tabs: {
      login: string;
      register: string;
    };
    placeholders: {
      email: string;
      password: string;
    };
    loginPending: string;
    loginIdle: string;
    registerPending: string;
    registerIdle: string;
    forgotTrigger: string;
    forgotTitle: string;
    forgotDescription: string;
    forgotPending: string;
    forgotIdle: string;
    forgotSuccess: string;
    backToLogin: string;
    resetPageTitle: string;
    resetPageDescription: string;
    resetPending: string;
    resetIdle: string;
    resetSuccessTitle: string;
    resetSuccessDescription: string;
    resetInvalid: string;
    resetRequestFresh: string;
    loginFallbackError: string;
    registerFallbackError: string;
    forgotFallbackError: string;
    resetFallbackError: string;
  };
  dashboard: {
    signedOutTitle: string;
    signedOutDescription: string;
    signedOutCta: string;
    eyebrow: string;
    title: (email: string) => string;
    description: string;
    primaryCta: string;
    adminCta: string;
    signOutCta: string;
    metrics: {
      totalMinutes: string;
      currentStreak: string;
      sessionsThisWeek: string;
      cardsCompleted: string;
    };
    weekTitle: string;
    likedSaved: (count: number) => string;
    recommendedTitle: string;
    coursesTitle: string;
    coursesDescription: string;
    coursesContinue: string;
    recentTitle: string;
    likedTitle: string;
    likedEmpty: string;
  };
  courses: {
    gateTitle: string;
    gateDescription: string;
    gateCta: string;
    heroEyebrow: string;
    heroTitle: string;
    heroDescription: string;
    resumeLabel: string;
    startLabel: string;
    completedLabel: string;
    modulesLabel: (count: number) => string;
    cardsLabel: string;
    continueCta: string;
    startCta: string;
    replayCta: string;
    playerBack: string;
    moduleLabel: (current: number, total: number) => string;
    completionTitle: string;
    completionDescription: string;
    nextModuleCta: string;
    libraryCta: string;
    finishedCourseTitle: string;
    finishedCourseDescription: string;
  };
  admin: {
    signedOutTitle: string;
    signedOutDescription: string;
    signedOutCta: string;
    forbiddenTitle: string;
    forbiddenDescription: string;
    backCta: string;
    eyebrow: string;
    title: string;
    description: string;
    sections: {
      overview: string;
      content: string;
      users: string;
    };
    metrics: {
      totalUsers: string;
      adminUsers: string;
      runsToday: string;
      savedHits: string;
      contentRuns: string;
      generatedCards: string;
    };
    topTopics: string;
    recentAudit: string;
    recentSessions: string;
    hotTopics: string;
    recentCards: string;
    usersTitle: string;
    makeAdmin: string;
    removeAdmin: string;
    noAudit: string;
    noCards: string;
  };
  errors: {
    appCrashTitle: string;
    appCrashRetry: string;
    missingPageTitle: string;
    missingPageDescription: string;
    noCards: string;
    generateFailureCard: string;
    rateLimit: string;
    genericGenerate: string;
  };
  future: {
    onboarding: Array<{ title: string; description: string }>;
    streaks: string[];
    notifications: string[];
    share: {
      card: string[];
      invite: string[];
    };
  };
}

export const appCopy: AppCopy = {
  voice: {
    principles: [
      "Keep every line short, punchy, and scroll-native.",
      "Favor reward, momentum, curiosity, and low effort.",
      "Sound confident and modern without tipping into cringe.",
    ],
    avoid: [
      "Avoid classroom language like learn, study, lesson, course, or education.",
      "Avoid long setup text and product-jargon filler.",
      "Avoid over-celebrating every action with noisy gamification.",
    ],
  },
  home: {
    badge: "FocusFeed",
    utilityBadge: "Scroll smarter",
    heroEyebrow: "Better than doomscrolling",
    heroTitle: "One more swipe. One more win.",
    heroDescription:
      "Pick a topic and drop into a fast, rewarding feed that actually gives you something back.",
    heroChips: ["Quick-hit card feed", "Fresh angles every round"],
    footerEmpty: "Pick a lane and start rolling.",
    footerSaved: (count) => `${count} saved hits waiting for a replay`,
    dashboardButton: "Your recap",
    coursesButton: "Courses",
    adminButton: "Admin",
    signOutButton: "Sign out",
    coursesTeaserEyebrow: "Locked guided runs",
    coursesTeaserTitle: "60-card courses built to pull you in.",
    coursesTeaserDescription:
      "Six sharp modules. Ten quick hits each. Structured enough to finish. Smooth enough to binge.",
    coursesTeaserCta: "Unlock courses",
    coursesTeaserMeta: "6 topics / 60 cards / signup to unlock",
  },
  topicInput: {
    eyebrow: "Start here",
    title: "What are we scrolling into?",
    placeholder: "Try lucid dreaming, dark matter, biohacking...",
    submitIdle: "Start scrolling smarter",
    submitLoading: "Loading your feed...",
    surprise: "Hit me with one",
    popularLabel: "Hot right now",
  },
  loading: {
    defaultMessage: "Stacking your next run...",
    customMessage: (topic) => `Lining up a fresh run on ${topic}...`,
    eyebrow: "Feed is warming up",
    title: "Your next stack is almost here",
    helper: "Fresh cards, quick turns, and a clean flow are loading now.",
  },
  card: {
    topicEyebrow: "Now spinning",
    loadingBadge: "Loading",
    styleHint: "Open theme controls",
    styleHintDesktop: "Theme the vibe",
    previousHint: "Swipe right for the last one",
    nextHint: "Swipe left for the next hit",
    helper: "One clean hit at a time. Double tap to save it.",
    saveLabel: "Save hit",
    unsaveLabel: "Saved",
    cardLabel: (index, total) => `Hit ${index} of ${total}`,
    controlsTitle: "Pick your vibe",
    controlsDescription: "Tweak the look without breaking the run.",
    controlsCloseHint: "Tap outside to close",
    controls: {
      backdropTitle: "Backdrop",
      typeTitle: "Type mood",
      contrastTitle: "Text pop",
      themes: {
        midnight: "Midnight",
        aurora: "Aurora",
        ember: "Ember",
        petal: "Petal",
        sage: "Sage",
      },
      fonts: {
        sans: "Sans",
        display: "Display",
        mono: "Mono",
      },
      textTones: {
        default: "Default",
        soft: "Soft",
        "high-contrast": "Sharp",
      },
    },
  },
  options: {
    eyebrow: "Keep it moving",
    title: (topic) => `Pick the next lane for ${topic}`,
    description:
      "Take a fresh angle, spin the topic again, or jump into a different branch.",
    followUpsCount: (count) => `${count} next-up picks`,
    noFollowUps: "More picks coming up",
    mobileHint: "Scroll for more lanes.",
    selectCta: "Jump into this lane",
    regenerate: "Run this topic again",
    explore: "Show me fresh lanes",
    empty: "No side paths showed up this round. Spin it again or switch lanes.",
  },
  auth: {
    trigger: "Keep your streak",
    title: "Keep your run alive",
    description: "Save your hits, hold your streak, and come back to your recap anytime.",
    tabs: {
      login: "Sign in",
      register: "Create account",
    },
    placeholders: {
      email: "Email",
      password: "Password",
    },
    loginPending: "Signing you in...",
    loginIdle: "Jump back in",
    registerPending: "Setting you up...",
    registerIdle: "Start your streak",
    forgotTrigger: "Forgot password?",
    forgotTitle: "Get back in fast",
    forgotDescription: "Drop your email and we'll send a clean way back to your next stack.",
    forgotPending: "Sending your link...",
    forgotIdle: "Send reset link",
    forgotSuccess:
      "If that email is in the app, a reset link is on the way. Check spam too if it doesn't land fast.",
    backToLogin: "Back to sign in",
    resetPageTitle: "Reset your password",
    resetPageDescription: "Set a new password and pick up your run.",
    resetPending: "Saving your new password...",
    resetIdle: "Set new password",
    resetSuccessTitle: "You're back in range.",
    resetSuccessDescription: "Your password is reset. Jump back in and keep the streak moving.",
    resetInvalid: "That reset link is expired or off. Grab a fresh one and try again.",
    resetRequestFresh: "Request a fresh link",
    loginFallbackError: "Couldn't sign you in.",
    registerFallbackError: "Couldn't create your account.",
    forgotFallbackError: "Couldn't send that link right now.",
    resetFallbackError: "Couldn't reset your password.",
  },
  dashboard: {
    signedOutTitle: "Sign in to see your recap.",
    signedOutDescription: "Your streak, saved hits, and favorite lanes will show up here.",
    signedOutCta: "Back to the feed",
    eyebrow: "Your recap",
    title: (email) => `You're back, ${email}`,
    description: "A quick run today keeps the streak alive.",
    primaryCta: "Back to the feed",
    adminCta: "Open admin",
    signOutCta: "Sign out",
    metrics: {
      totalMinutes: "Time stacked",
      currentStreak: "Streak",
      sessionsThisWeek: "Runs this week",
      cardsCompleted: "Hits cleared",
    },
    weekTitle: "This week",
    likedSaved: (count) => `${count} saved hits`,
    recommendedTitle: "Next lanes",
    coursesTitle: "Guided runs",
    coursesDescription: "Structured 60-card stacks built to finish, not just skim.",
    coursesContinue: "Continue course",
    recentTitle: "Recent lanes",
    likedTitle: "Saved hits",
    likedEmpty: "Save a few hits in the feed and they'll land here.",
  },
  courses: {
    gateTitle: "Sign in to open courses.",
    gateDescription: "Guided runs stay tied to your account so your place is always waiting.",
    gateCta: "Back to the feed",
    heroEyebrow: "Guided runs",
    heroTitle: "Short courses. Cleaner momentum.",
    heroDescription: "Pick a 60-card run, clear one module at a time, and keep your progress tight.",
    resumeLabel: "Resume",
    startLabel: "Start",
    completedLabel: "Completed",
    modulesLabel: (count: number) => `${count} modules`,
    cardsLabel: "10 cards each",
    continueCta: "Keep going",
    startCta: "Start course",
    replayCta: "Replay",
    playerBack: "Back to courses",
    moduleLabel: (current: number, total: number) => `Module ${current} / ${total}`,
    completionTitle: "Module cleared.",
    completionDescription: "Clean run. The next module is ready when you are.",
    nextModuleCta: "Next module",
    libraryCta: "Course library",
    finishedCourseTitle: "Course complete.",
    finishedCourseDescription: "You cleared the full run. Replay it or jump into another lane.",
  },
  admin: {
    signedOutTitle: "Sign in to open admin.",
    signedOutDescription: "Admin tools only show up once your account is signed in and approved.",
    signedOutCta: "Back to the feed",
    forbiddenTitle: "This lane is admin-only.",
    forbiddenDescription: "Your account is live, but it doesn't have admin access on the server.",
    backCta: "Back to the feed",
    eyebrow: "Admin",
    title: "Control room",
    description: "Track the feed, inspect users, and keep an eye on what is moving.",
    sections: {
      overview: "Overview",
      content: "Content",
      users: "Users",
    },
    metrics: {
      totalUsers: "Users",
      adminUsers: "Admins",
      runsToday: "Active today",
      savedHits: "Saved hits",
      contentRuns: "Total runs",
      generatedCards: "Generated cards",
    },
    topTopics: "Top topics",
    recentAudit: "Recent admin moves",
    recentSessions: "Latest runs",
    hotTopics: "Hot topics",
    recentCards: "Recent generated cards",
    usersTitle: "User access",
    makeAdmin: "Make admin",
    removeAdmin: "Remove admin",
    noAudit: "No admin activity yet.",
    noCards: "No generated cards have been stored yet.",
  },
  errors: {
    appCrashTitle: "The feed hit a snag",
    appCrashRetry: "Reload the vibe",
    missingPageTitle: "That page slipped away",
    missingPageDescription: "Head back and pick up the scroll from there.",
    noCards: "Nothing queued up yet.",
    generateFailureCard: "Couldn't load this run. Check your connection and give it another go.",
    rateLimit: "Too many fresh pulls at once. Give it a second.",
    genericGenerate: "Couldn't load that run. Try again.",
  },
  future: {
    onboarding: [
      {
        title: "Pick your lane",
        description: "Drop into anything you're curious about.",
      },
      {
        title: "Swipe for quick hits",
        description: "Fast cards, no heavy setup.",
      },
      {
        title: "Save the good ones",
        description: "Keep your best hits and build a streak.",
      },
    ],
    streaks: [
      "Still rolling.",
      "Your streak looks good.",
      "One quick run keeps it alive.",
    ],
    notifications: [
      "Your next stack is waiting.",
      "A quick run beats a doomscroll.",
      "Come back for one more hit.",
    ],
    share: {
      card: [
        "Found this on FocusFeed. Better than a dead scroll.",
        "Quick hit from FocusFeed.",
      ],
      invite: [
        "Trade your doomscroll for something better.",
        "Come try FocusFeed for a smarter scroll.",
      ],
    },
  },
};
